import { useState } from "react";
import { Check, Lock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { DesignStyle } from "@/data/designStyles";
import { cn } from "@/lib/utils";
import { useLockedStylesFlag } from "@/hooks/useFeatureFlag";
import { UpgradeProDialog } from "@/components/UpgradeProDialog";

interface StyleGridProps {
  styles: DesignStyle[];
  selectedStyle: string | null;
  onSelect: (styleName: string) => void;
  loading?: boolean;
}

// Fallback gradient colors for styles without images
const fallbackGradients = [
  "from-amber-200 to-orange-300",
  "from-emerald-200 to-teal-300",
  "from-blue-200 to-indigo-300",
  "from-purple-200 to-pink-300",
  "from-rose-200 to-red-300",
  "from-cyan-200 to-blue-300",
  "from-lime-200 to-green-300",
  "from-yellow-200 to-amber-300",
];

const StyleCard = ({
  style,
  index,
  isSelected,
  locked,
  onSelect,
}: {
  style: DesignStyle;
  index: number;
  isSelected: boolean;
  locked: boolean;
  onSelect: () => void;
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const showImage = style.image && !imageError;
  const showFallback = !style.image || imageError;

  return (
    <button
      onClick={onSelect}
      className={cn(
        "relative flex-shrink-0 w-40 h-44 rounded-xl overflow-hidden transition-all",
        isSelected
          ? "ring-2 ring-primary ring-offset-2 scale-[0.98]"
          : "hover:scale-[0.98]"
      )}
    >
      {showImage && (
        <>
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-muted to-muted-foreground/20 animate-pulse" />
          )}
          <img
            src={style.image}
            alt={style.name}
            className={cn(
              "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
              imageLoaded ? "opacity-100" : "opacity-0",
              locked && "grayscale-[40%]"
            )}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        </>
      )}

      {showFallback && (
        <div
          className={cn(
            "absolute inset-0 bg-gradient-to-br",
            fallbackGradients[index % fallbackGradients.length],
            locked && "opacity-70"
          )}
        />
      )}

      {/* Dark Overlay for Text Readability */}
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent",
          locked && "from-black/75 via-black/50"
        )}
      />

      {/* Style Name and Description */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-white font-semibold text-base text-left">
          {style.name}
        </p>
        <p className="text-white/70 text-xs text-left line-clamp-2 mt-0.5">
          {style.description}
        </p>
      </div>

      {/* Selected Indicator */}
      {isSelected && !locked && (
        <div className="absolute top-2 right-2 h-6 w-6 bg-primary rounded-full flex items-center justify-center">
          <Check className="h-4 w-4 text-primary-foreground" />
        </div>
      )}

      {/* Lock badge */}
      {locked && (
        <div className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
          <Lock className="h-3 w-3" />
          Pro
        </div>
      )}
    </button>
  );
};

export const StyleGrid = ({
  styles,
  selectedStyle,
  onSelect,
  loading = false,
}: StyleGridProps) => {
  const { isStyleLocked } = useLockedStylesFlag();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [lockedStyleName, setLockedStyleName] = useState<string>("");

  const handleSelect = (styleName: string) => {
    if (isStyleLocked(styleName)) {
      setLockedStyleName(styleName);
      setUpgradeOpen(true);
      return;
    }
    onSelect(styleName);
  };

  if (loading) {
    return (
      <div
        className="flex gap-3 overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="flex-shrink-0 w-40 h-44 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div
        className="flex gap-3 overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {styles.map((style, index) => (
          <StyleCard
            key={style.name}
            style={style}
            index={index}
            isSelected={selectedStyle === style.name}
            locked={isStyleLocked(style.name)}
            onSelect={() => handleSelect(style.name)}
          />
        ))}
      </div>

      <UpgradeProDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        styleName={lockedStyleName}
      />
    </>
  );
};
